import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../../components/atlas/PageHeader';
import { PipelineBoard } from '../components/PipelineBoard';
import { PipelineList } from '../components/PipelineList';
import { PipelineCalendar } from '../components/PipelineCalendar';
import { LeadDetailDrawer } from '../components/LeadDetailDrawer';

type ViewMode = 'board' | 'list' | 'calendar';

const VIEWS: { id: ViewMode; label: string }[] = [
  { id: 'board', label: 'Board' },
  { id: 'list', label: 'Lista' },
  { id: 'calendar', label: 'Calendário' },
];

function parseView(value: string | null): ViewMode {
  if (value === 'list' || value === 'calendar') {
    return value;
  }
  return 'board';
}

export function PipelinePage() {
  const [params, setParams] = useSearchParams();
  const view = parseView(params.get('view'));
  const filter = params.get('filter');
  const leadIdParam = params.get('lead');
  const [leadId, setLeadId] = useState<number | null>(leadIdParam ? Number(leadIdParam) : null);

  useEffect(() => {
    setLeadId(leadIdParam ? Number(leadIdParam) : null);
  }, [leadIdParam]);

  function setView(v: ViewMode) {
    const next = new URLSearchParams(params);
    next.set('view', v);
    // Filter is only meaningful for the list view; clear it otherwise
    if (v !== 'list') {
      next.delete('filter');
    }
    setParams(next, { replace: true });
  }

  function openLead(id: number) {
    const next = new URLSearchParams(params);
    next.set('lead', String(id));
    setParams(next, { replace: true });
  }

  function closeLead() {
    const next = new URLSearchParams(params);
    next.delete('lead');
    setParams(next, { replace: true });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Pipeline"
        title="Seus leads"
        subtitle="Board para mover, lista para focar, calendário para planejar."
        actions={
          <div className="inline-flex bg-subtle rounded-lg p-1 gap-1">
            {VIEWS.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setView(v.id)}
                className={`text-sm px-3 py-1 rounded-md transition-colors ${
                  view === v.id
                    ? 'bg-surface text-ink-primary shadow-sm font-medium'
                    : 'text-ink-secondary hover:text-ink-primary'
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
        }
      />

      {view === 'board' && <PipelineBoard onLeadClick={openLead} />}
      {view === 'list' && (
        <PipelineList filter={filter === 'due' ? 'due' : 'all'} onLeadClick={openLead} />
      )}
      {view === 'calendar' && <PipelineCalendar onLeadClick={openLead} />}

      <LeadDetailDrawer leadId={leadId} open={!!leadId} onClose={closeLead} />
    </div>
  );
}
